import * as THREE from 'three';
import { COCKPIT_CONTROLS } from '../core/SimConfig.js';
import { AXIS, DEG2RAD } from '../utils.js';

const _q = new THREE.Quaternion();
const _qPitch = new THREE.Quaternion();
const _qRoll = new THREE.Quaternion();

/**
 * Animates the pilot-operated cockpit hardware (throttle, control stick,
 * gear lever) from aircraft inputs. Throttle and stick use a spring-damper
 * for a hand-on-controls feel; the gear lever interpolates linearly toward
 * its on/off target. Only ticks while the cockpit camera is active.
 */
export default class CockpitControls {
  constructor({ aircraft, model, cameraManager }) {
    this.aircraft = aircraft;
    this.cameraManager = cameraManager;

    // ── Throttle (slides along a local axis) ────────────────────────────
    const tCfg = COCKPIT_CONTROLS.throttle;
    const throttleMesh = model.getObjectByName(tCfg.mesh);
    if (throttleMesh) {
      // Preserve rest pose across scenario reloads (same model object reused)
      if (!throttleMesh.userData._originalQuat) {
        throttleMesh.userData._originalQuat = throttleMesh.quaternion.clone();
        throttleMesh.userData._originalPos = throttleMesh.position.clone();
      }
      const baseQuat = throttleMesh.userData._originalQuat.clone();
      const basePos = throttleMesh.userData._originalPos.clone();
      throttleMesh.quaternion.copy(baseQuat);
      throttleMesh.position.copy(basePos);

      // Slide direction in parent space — local axis rotated by rest pose,
      // so authoring can use any throttle orientation.
      const slideDir = AXIS[tCfg.axis].clone().applyQuaternion(baseQuat);

      this.throttle = {
        cfg: tCfg,
        mesh: throttleMesh,
        baseQuat,
        basePos,
        slideDir,
        offset: this._throttleTarget(),  // snap to current input — no startup glide
        offsetVel: 0,
      };
    } else {
      console.warn(`CockpitControls: mesh "${tCfg.mesh}" not found`);
      this.throttle = null;
    }

    // ── Control stick (two-axis spring-damper) ──────────────────────────
    const sCfg = COCKPIT_CONTROLS.stick;
    const stickMesh = model.getObjectByName(sCfg.mesh);
    if (stickMesh) {
      if (!stickMesh.userData._originalQuat) {
        stickMesh.userData._originalQuat = stickMesh.quaternion.clone();
      }
      const baseQuat = stickMesh.userData._originalQuat.clone();
      stickMesh.quaternion.copy(baseQuat);

      this.stick = {
        cfg: sCfg,
        mesh: stickMesh,
        baseQuat,
        pitch: this._stickPitchTarget(),
        pitchVel: 0,
        roll: this._stickRollTarget(),
        rollVel: 0,
      };
    } else {
      console.warn(`CockpitControls: mesh "${sCfg.mesh}" not found`);
      this.stick = null;
    }

    // ── Gear lever (linear chase to on/off target) ──────────────────────
    const gCfg = COCKPIT_CONTROLS.gear;
    const gearMesh = model.getObjectByName(gCfg.mesh);
    if (gearMesh) {
      if (!gearMesh.userData._originalQuat) {
        gearMesh.userData._originalQuat = gearMesh.quaternion.clone();
      }
      const baseQuat = gearMesh.userData._originalQuat.clone();
      gearMesh.quaternion.copy(baseQuat);

      this.gear = {
        cfg: gCfg,
        mesh: gearMesh,
        baseQuat,
        // 0..1, linearly chases aircraft.gearTarget
        position: aircraft.gearTarget ?? 0,
      };
    } else {
      console.warn(`CockpitControls: mesh "${gCfg.mesh}" not found`);
      this.gear = null;
    }
  }

  _throttleTarget() {
    const t = COCKPIT_CONTROLS.throttle;
    return (this.aircraft.throttle ?? 0) * t.travel * t.direction;
  }

  _stickPitchTarget() {
    const s = COCKPIT_CONTROLS.stick;
    return (this.aircraft.elevator ?? 0) * s.pitchMaxDeg * DEG2RAD * s.pitchDirection;
  }

  _stickRollTarget() {
    const s = COCKPIT_CONTROLS.stick;
    return (this.aircraft.ailerons ?? 0) * s.rollMaxDeg * DEG2RAD * s.rollDirection;
  }

  update(dt) {
    const inCockpit = !!this.cameraManager?.cockpitView;

    // Out of cockpit: snap state to current targets (no spring catch-up on
    // re-entry). Skip transform work entirely.
    if (!inCockpit) {
      if (this.throttle) {
        this.throttle.offset = this._throttleTarget();
        this.throttle.offsetVel = 0;
      }
      if (this.stick) {
        this.stick.pitch = this._stickPitchTarget();
        this.stick.roll = this._stickRollTarget();
        this.stick.pitchVel = 0;
        this.stick.rollVel = 0;
      }
      if (this.gear) {
        this.gear.position = this.aircraft.gearTarget ?? 0;
      }
      return;
    }

    const kDefault = COCKPIT_CONTROLS.stiffness;
    const cDefault = COCKPIT_CONTROLS.damping;

    // ── Throttle — spring-damper toward target offset ───────────────────
    if (this.throttle) {
      const t = this.throttle;
      const k = t.cfg.stiffness ?? kDefault;
      const c = t.cfg.damping ?? cDefault;
      const target = this._throttleTarget();
      // Semi-implicit Euler on a damped harmonic oscillator (unit mass)
      const accel = k * (target - t.offset) - c * t.offsetVel;
      t.offsetVel += accel * dt;
      t.offset += t.offsetVel * dt;
      t.mesh.position.copy(t.basePos).addScaledVector(t.slideDir, t.offset);
    }

    // ── Stick — two-axis spring-damper ──────────────────────────────────
    if (this.stick) {
      const s = this.stick;
      const k = s.cfg.stiffness ?? kDefault;
      const c = s.cfg.damping ?? cDefault;

      const targetPitch = this._stickPitchTarget();
      const targetRoll = this._stickRollTarget();

      const accelP = k * (targetPitch - s.pitch) - c * s.pitchVel;
      const accelR = k * (targetRoll - s.roll) - c * s.rollVel;
      s.pitchVel += accelP * dt;
      s.rollVel += accelR * dt;
      s.pitch += s.pitchVel * dt;
      s.roll += s.rollVel * dt;

      // Roll on the gimbal's outer ring, pitch on the inner pivot — same
      // convention as the attitude ball so axes calibrate consistently.
      _qRoll.setFromAxisAngle(AXIS[s.cfg.rollAxis], s.roll);
      _qPitch.setFromAxisAngle(AXIS[s.cfg.pitchAxis], s.pitch);
      _q.copy(_qRoll).multiply(_qPitch);
      s.mesh.quaternion.copy(s.baseQuat).multiply(_q);
    }

    // ── Gear lever — linear chase to on/off target ──────────────────────
    if (this.gear) {
      const g = this.gear;
      const target = this.aircraft.gearTarget ?? 0;
      const delta = target - g.position;
      if (delta !== 0) {
        const step = Math.sign(delta) * Math.min(g.cfg.rate * dt, Math.abs(delta));
        g.position += step;
      }
      const angle = g.position * g.cfg.deployedAngleDeg * DEG2RAD * g.cfg.direction;
      _q.setFromAxisAngle(AXIS[g.cfg.axis], angle);
      g.mesh.quaternion.copy(g.baseQuat).multiply(_q);
    }
  }

  dispose() {
    // Restore rest pose so nothing is left mid-spring if the scenario tears
    // down while the cockpit is visible.
    if (this.throttle) {
      this.throttle.mesh.position.copy(this.throttle.basePos);
      this.throttle.mesh.quaternion.copy(this.throttle.baseQuat);
    }
    if (this.stick) this.stick.mesh.quaternion.copy(this.stick.baseQuat);
    if (this.gear) this.gear.mesh.quaternion.copy(this.gear.baseQuat);
  }
}
